// SPDX-License-Identifier: AGPL-3.0-or-later

import {PublicUserFlags} from '@fluxer/constants/src/UserConstants';

// Discord's privileged gateway intent bits. A full bit is an approval (migration or admin); a _LIMITED bit is what an
// owner turns on while the bot is in 100 guilds or fewer. Either bit grants the intent.
export const ApplicationFlags = {
	GATEWAY_PRESENCE: 1 << 12,
	GATEWAY_PRESENCE_LIMITED: 1 << 13,
	GATEWAY_GUILD_MEMBERS: 1 << 14,
	GATEWAY_GUILD_MEMBERS_LIMITED: 1 << 15,
	GATEWAY_MESSAGE_CONTENT: 1 << 18,
	GATEWAY_MESSAGE_CONTENT_LIMITED: 1 << 19,
} as const;

export const ApplicationFlagsDescriptions: Record<keyof typeof ApplicationFlags, string> = {
	GATEWAY_PRESENCE: 'Presence intent, approved',
	GATEWAY_PRESENCE_LIMITED: 'Presence intent, turned on by the owner while the bot is in 100 guilds or fewer',
	GATEWAY_GUILD_MEMBERS: 'Guild members intent, approved',
	GATEWAY_GUILD_MEMBERS_LIMITED: 'Guild members intent, turned on by the owner while the bot is in 100 guilds or fewer',
	GATEWAY_MESSAGE_CONTENT: 'Message content intent, approved',
	GATEWAY_MESSAGE_CONTENT_LIMITED:
		'Message content intent, turned on by the owner while the bot is in 100 guilds or fewer',
};

// Every application created before the intent gate has the three privileged intents.
export const LEGACY_INTENT_FLAGS =
	ApplicationFlags.GATEWAY_PRESENCE | ApplicationFlags.GATEWAY_GUILD_MEMBERS | ApplicationFlags.GATEWAY_MESSAGE_CONTENT;

export type PrivilegedIntent = 'presence' | 'guild_members' | 'message_content';

const INTENT_BITS: Record<PrivilegedIntent, number> = {
	presence: ApplicationFlags.GATEWAY_PRESENCE | ApplicationFlags.GATEWAY_PRESENCE_LIMITED,
	guild_members: ApplicationFlags.GATEWAY_GUILD_MEMBERS | ApplicationFlags.GATEWAY_GUILD_MEMBERS_LIMITED,
	message_content: ApplicationFlags.GATEWAY_MESSAGE_CONTENT | ApplicationFlags.GATEWAY_MESSAGE_CONTENT_LIMITED,
};

// A row without the column (written before it existed, or before the backfill reached it) reads as the legacy grant.
export function resolveApplicationIntentFlags(stored: number | null | undefined): number {
	return stored ?? LEGACY_INTENT_FLAGS;
}

export function allowsIntent(flags: number, intent: PrivilegedIntent): boolean {
	return (flags & INTENT_BITS[intent]) !== 0;
}
export const BotFlags = {
	FRIENDLY_BOT: PublicUserFlags.FRIENDLY_BOT,
	FRIENDLY_BOT_MANUAL_APPROVAL: PublicUserFlags.FRIENDLY_BOT_MANUAL_APPROVAL,
} as const;
export const BotFlagsDescriptions: Record<keyof typeof BotFlags, string> = {
	FRIENDLY_BOT: 'Bot accepts friend requests from users',
	FRIENDLY_BOT_MANUAL_APPROVAL: 'Bot requires manual approval for friend requests',
};
